// src/services/postGraphileService.jsx
import { checkAndRefreshToken, withBearer } from "@/services/auth"

const postGraphile = {
  /**
   * Fetch with automatic token refresh for a GraphQL query string.
   *
   * @param {string} fetchUrl - GraphQL endpoint URL.
   * @param {string} graphqlQuery - GraphQL query string.
   * @param {{ token?: string, environment?: { auth?: { url?: string } } }} accessContext
   * @param {(updater: (prev: any) => any) => void} setAccessContext
   * @param {string} [errorText]
   * @returns {Promise<any>}
   * @throws {Error}
   */
  async fetchRequestWithTokenRefresh(fetchUrl, graphqlQuery, accessContext, setAccessContext, errorText) {
    const accessToken = await checkAndRefreshToken(accessContext, setAccessContext)
    if (!accessToken) throw new Error("Failed to get access token")
    const requestData = this.httpRequestData(graphqlQuery, accessToken)
    return this.fetchRequest(fetchUrl, requestData, errorText)
  },

  /**
   * Fetch with automatic token refresh for arbitrary RequestInit options.
   *
   * @param {string | URL} fetchUrl
   * @param {RequestInit} requestOptions
   * @param {{ token?: string, environment?: { auth?: { url?: string } } }} accessContext
   * @param {(updater: (prev: any) => any) => void} setAccessContext
   * @param {string} [errorText]
   * @returns {Promise<any>}
   * @throws {Error}
   */
  async fetchRequestWithTokenRefreshGeneric(fetchUrl, requestOptions, accessContext, setAccessContext, errorText) {
    const accessToken = await checkAndRefreshToken(accessContext, setAccessContext)
    if (!accessToken) throw new Error("Failed to get access token")
    const opts = withBearer(requestOptions, accessToken)
    return this.fetchRequest(fetchUrl, opts, errorText)
  },

  /**
   * Perform a fetch request and parse response as JSON or text.
   *
   * @param {RequestInfo | URL} fetchUrl
   * @param {RequestInit} request
   * @param {string} [errorText]
   * @returns {Promise<any>}
   * @throws {Error & { status?: number, statusText?: string, bodySnippet?: string }}
   */
  async fetchRequest(fetchUrl, request, errorText) {
    const res = await fetch(fetchUrl, request)

    if (!res.ok) {
      let bodyText = ""
      try {
        // Try to read error body (text), include a short snippet in the thrown error.
        bodyText = await res.text()
      } catch {}
      // Prefer a user-facing message the API intentionally returned (FastAPI puts
      // it in `detail`); fall back to the generic errorText otherwise. This makes
      // 4xx rule violations (e.g. activating a route under an archived voyage)
      // surface their real message to the caller instead of a generic label.
      let detailMessage = ""
      try {
        const parsed = JSON.parse(bodyText)
        if (typeof parsed?.detail === "string") detailMessage = parsed.detail
      } catch {}
      const err = new Error(detailMessage || errorText || res.statusText)
      err.status = res.status
      err.statusText = res.statusText
      err.bodySnippet = bodyText.slice(0, 500)
      throw err
    }

    const ct = res.headers.get("content-type") || ""
    return ct.includes("application/json") ? res.json() : res.text()
  },

  /**
   * Build a GraphQL POST request with JSON body and optional Authorization.
   *
   * @param {string} graphqlQuery
   * @param {string} [accessToken]
   * @returns {RequestInit}
   */
  httpRequestData(graphqlQuery, accessToken = undefined) {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
    }
    if (accessToken !== undefined) {
      headers["Authorization"] = `Bearer ${accessToken}`
    }
    return {
      method: "POST",
      headers,
      body: JSON.stringify({ query: graphqlQuery }),
    }
  },

  /** Key under which GraphQL data is returned in PostGraphile responses. */
  stateKey: "data",
}

export default postGraphile
